import { useEffect, useState } from "react";
import { NavLink } from "react-router";
import {
  ArrowLeft,
  Loader2,
  Plus,
  RotateCcw,
  Save,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useGetPickupBundleDiscount,
  useSetPickupBundleDiscount,
  type PickupBundleTier,
} from "@/api/pickup-bundle-discount";
import { useGetCarriers } from "@/api/carriers";
import { useAdminCarrierCustomerTiers } from "@/api/customer-carrier-discounts";
import {
  type CarrierLabelDiscountRule,
  type CarrierLabelDiscountRuleInput,
  type CarrierDiscountFunding,
  useAdminCarrierLabelDiscountRules,
  useCreateAdminCarrierLabelDiscountRule,
  useDeleteAdminCarrierLabelDiscountRule,
  useUpdateAdminCarrierLabelDiscountRule,
} from "@/api/carrier-discount-rules";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Phase 16b (#269): admin editor for the pickup-bundle discount curve.
 *
 * The curve is a list of tiers ordered by `position` (0-indexed). Each tier
 * says: when the Nth shipment attaches to a bundled pickup booking, take
 * `fractionOff * raw` off. Position 0 is the 1st shipment and is always
 * read-only at fractionOff = 0 (the 1st-shipment anchor).
 *
 * Save is full-replacement via PUT — the server deletes existing rows and
 * inserts the provided set in one transaction. Validation (range, anchor,
 * contiguity) happens client-side AND server-side.
 */
interface DraftTier {
  position: number;
  fractionOff: number;
  label: string | null;
}

const DEFAULT_TIERS: DraftTier[] = [
  { position: 0, fractionOff: 0, label: null },
  { position: 1, fractionOff: 0.5, label: null },
  { position: 2, fractionOff: 0.75, label: null },
];

const SAMPLE_PICKUP_CENTS = 500;

export function PickupBundleDiscountEditor() {
  const { data: carriersResult, isLoading: carriersLoading } = useGetCarriers({
    active: true,
  });
  const carriers = carriersResult?.data ?? [];
  const [selectedCarrierId, setSelectedCarrierId] = useState<string | null>(
    null
  );
  const { data, isLoading } = useGetPickupBundleDiscount(selectedCarrierId);
  const save = useSetPickupBundleDiscount();
  const [draft, setDraft] = useState<DraftTier[]>([]);

  useEffect(() => {
    if (!selectedCarrierId && carriers.length > 0) {
      setSelectedCarrierId(carriers[0].id);
    }
  }, [carriers, selectedCarrierId]);

  // Hydrate draft from server when it loads.
  useEffect(() => {
    if (data) {
      setDraft(
        data.length > 0
          ? data.map((t: PickupBundleTier) => ({
              position: t.position,
              fractionOff: t.fractionOff,
              label: t.label,
            }))
          : DEFAULT_TIERS
      );
    }
  }, [data]);

  const serverDraft =
    data?.map((t) => ({
      position: t.position,
      fractionOff: t.fractionOff,
      label: t.label,
    })) ?? [];
  const dirty = !!data && JSON.stringify(draft) !== JSON.stringify(serverDraft);

  const updateFraction = (position: number, percent: number) => {
    setDraft((prev) =>
      prev.map((t) =>
        t.position === position
          ? { ...t, fractionOff: Math.max(0, Math.min(100, percent)) / 100 }
          : t
      )
    );
  };

  const updateLabel = (position: number, label: string) => {
    setDraft((prev) =>
      prev.map((t) =>
        t.position === position ? { ...t, label: label || null } : t
      )
    );
  };

  const addTier = () => {
    setDraft((prev) => {
      const lastFraction = prev[prev.length - 1]?.fractionOff ?? 0;
      const nextFraction = Math.min(
        1,
        Math.round(lastFraction * 100 + 10) / 100
      );
      return [
        ...prev,
        {
          position: prev.length,
          fractionOff: nextFraction,
          label: null,
        },
      ];
    });
  };

  const removeTier = (position: number) => {
    if (position === 0) return; // pos-0 anchor is immortal
    setDraft((prev) =>
      prev
        .filter((t) => t.position !== position)
        .map((t, idx) => ({ ...t, position: idx }))
    );
  };

  const resetToDefault = () => {
    setDraft(DEFAULT_TIERS);
  };

  const handleSave = () => {
    if (!selectedCarrierId) {
      toast.error("Select a carrier first.");
      return;
    }
    // Client-side mirror of server validation, so admins see issues without a round-trip.
    if (draft.length === 0) {
      toast.error("At least one tier is required.");
      return;
    }
    if (draft[0].position !== 0 || draft[0].fractionOff !== 0) {
      toast.error("Position 0 must exist with fractionOff = 0.");
      return;
    }
    for (let i = 0; i < draft.length; i++) {
      if (draft[i].position !== i) {
        toast.error(`Positions must be contiguous from 0 (gap at index ${i}).`);
        return;
      }
      if (draft[i].fractionOff < 0 || draft[i].fractionOff > 1) {
        toast.error(`Tier ${i}: discount must be between 0% and 100%.`);
        return;
      }
    }

    save.mutate(
      {
        carrierId: selectedCarrierId,
        tiers: draft.map((t) => ({
          position: t.position,
          fractionOff: t.fractionOff,
          label: t.label,
        })),
      },
      {
        onSuccess: () => {
          toast.success("Curve saved.");
        },
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error(e.response?.data?.error ?? "Failed to save curve.");
        },
      }
    );
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="-ml-2 mb-1 h-7 px-2"
          >
            <NavLink to="/pricing/discounts">
              <ArrowLeft className="h-3 w-3 mr-1" />
              Discounts
            </NavLink>
          </Button>
          <h1 className="text-3xl font-bold">Bundle pickup</h1>
          <p className="text-gray-600 mt-2 max-w-2xl text-sm">
            When multiple shipments share a single pickup booking (one driver
            visit), each additional shipment earns a discount on its pickup fee.
            Discount allocates at attach-time and is{" "}
            <strong>sticky on detach</strong> — once a shipment claims its tier,
            sibling detaches don't renumber siblings.
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
          <Select
            value={selectedCarrierId ?? undefined}
            onValueChange={setSelectedCarrierId}
            disabled={carriersLoading || save.isPending}
          >
            <SelectTrigger className="w-[260px]">
              <SelectValue placeholder="Select carrier" />
            </SelectTrigger>
            <SelectContent>
              {carriers.map((carrier) => (
                <SelectItem key={carrier.id} value={carrier.id}>
                  {carrier.name} ({carrier.code})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={resetToDefault}>
            <RotateCcw className="h-3 w-3 mr-1" />
            Reset to default
          </Button>
          <Button
            onClick={handleSave}
            disabled={!selectedCarrierId || !dirty || save.isPending}
            size="sm"
          >
            <Save className="h-3 w-3 mr-1" />
            {save.isPending ? "Saving…" : "Save curve"}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Tier curve</CardTitle>
        </CardHeader>
        <CardContent>
          {carriersLoading || isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[120px]">Position</TableHead>
                  <TableHead className="w-[140px]">Discount</TableHead>
                  <TableHead>
                    Preview on a ${(SAMPLE_PICKUP_CENTS / 100).toFixed(2)} raw
                    pickup
                  </TableHead>
                  <TableHead>Label (optional)</TableHead>
                  <TableHead className="w-[1%]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {draft.map((tier) => {
                  const isAnchor = tier.position === 0;
                  const discountCents = Math.round(
                    SAMPLE_PICKUP_CENTS * tier.fractionOff
                  );
                  const netCents = SAMPLE_PICKUP_CENTS - discountCents;
                  return (
                    <TableRow key={tier.position}>
                      <TableCell className="font-medium">
                        {isAnchor
                          ? "1st shipment"
                          : tier.position === 1
                            ? "2nd shipment"
                            : tier.position === 2
                              ? "3rd shipment"
                              : `${tier.position + 1}th shipment`}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            step="1"
                            min={0}
                            max={100}
                            value={Math.round(tier.fractionOff * 100)}
                            onChange={(e) =>
                              updateFraction(
                                tier.position,
                                Number(e.target.value)
                              )
                            }
                            disabled={isAnchor}
                            className="h-8 w-20 text-right tabular-nums"
                          />
                          <span className="text-sm text-muted-foreground">
                            %
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {isAnchor ? (
                          <span>
                            Full price ·{" "}
                            <span className="font-mono">
                              ${(SAMPLE_PICKUP_CENTS / 100).toFixed(2)}
                            </span>
                          </span>
                        ) : (
                          <span>
                            <span className="text-rose-700 font-mono">
                              −${(discountCents / 100).toFixed(2)}
                            </span>{" "}
                            →{" "}
                            <span className="font-mono">
                              ${(netCents / 100).toFixed(2)}
                            </span>
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Input
                          type="text"
                          value={tier.label ?? ""}
                          onChange={(e) =>
                            updateLabel(tier.position, e.target.value)
                          }
                          placeholder="—"
                          className="h-8 text-sm"
                        />
                      </TableCell>
                      <TableCell>
                        {isAnchor ? (
                          <span
                            className="text-xs text-muted-foreground"
                            title="Position 0 is the 1st-shipment anchor and cannot be removed."
                          >
                            anchor
                          </span>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeTier(tier.position)}
                            className="h-8 w-8 p-0"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}

          <div className="mt-3 flex items-center justify-between">
            <Button variant="outline" size="sm" onClick={addTier}>
              <Plus className="h-3 w-3 mr-1" />
              Add tier
            </Button>
            <div className="text-xs text-muted-foreground">
              Position N applies when the Nth shipment attaches. Positions
              beyond the last row saturate at that row's discount.
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">
            Worked example
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-2">
            Five shipments on one pickup booking, each with a raw pickup price
            of $5.00:
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Shipment</TableHead>
                <TableHead>Tier</TableHead>
                <TableHead className="text-right">Net pickup</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[0, 1, 2, 3, 4].map((shipIdx) => {
                const tier =
                  draft.find((t) => t.position === shipIdx) ??
                  draft[draft.length - 1];
                const fraction = tier?.fractionOff ?? 0;
                const cents = Math.round(SAMPLE_PICKUP_CENTS * (1 - fraction));
                return (
                  <TableRow key={shipIdx}>
                    <TableCell>
                      Shipment {shipIdx + 1}
                      {shipIdx >= draft.length && (
                        <span className="ml-1 text-xs text-muted-foreground">
                          (saturated)
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {fraction === 0
                        ? "—"
                        : `${Math.round(fraction * 100)}% off`}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      ${(cents / 100).toFixed(2)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <LabelDiscountRulesEditor carrierId={selectedCarrierId} />
    </div>
  );
}

const ALL_CUSTOMERS = "__all_customers__";
const SAVED_RESTRICTIONS = "__saved_restrictions__";

interface LabelDiscountFormState {
  eligibilityRows: CarrierLabelDiscountRuleInput["eligibilityRows"] | null;
  funding: CarrierDiscountFunding | "";
  id: string | null;
  name: string;
  percentage: string;
  status: "DRAFT" | "ACTIVE" | "ARCHIVED";
  tierId: string;
}

const EMPTY_LABEL_DISCOUNT_FORM: LabelDiscountFormState = {
  eligibilityRows: null,
  funding: "",
  id: null,
  name: "",
  percentage: "10",
  status: "ACTIVE",
  tierId: ALL_CUSTOMERS,
};

function LabelDiscountRulesEditor({ carrierId }: { carrierId: string | null }) {
  const rules = useAdminCarrierLabelDiscountRules(carrierId);
  const tiers = useAdminCarrierCustomerTiers(carrierId);
  const createRule = useCreateAdminCarrierLabelDiscountRule();
  const updateRule = useUpdateAdminCarrierLabelDiscountRule();
  const deleteRule = useDeleteAdminCarrierLabelDiscountRule();
  const [form, setForm] = useState<LabelDiscountFormState>(
    EMPTY_LABEL_DISCOUNT_FORM
  );

  useEffect(() => {
    setForm(EMPTY_LABEL_DISCOUNT_FORM);
  }, [carrierId]);

  const tierNameById = new Map(
    (tiers.data ?? []).map((tier) => [tier.id, tier.name])
  );
  const isSaving = createRule.isPending || updateRule.isPending;

  const resetForm = () => {
    setForm(EMPTY_LABEL_DISCOUNT_FORM);
  };

  const editRule = (rule: CarrierLabelDiscountRule) => {
    const simpleTierId =
      rule.eligibilityRows.length === 1
        ? rule.eligibilityRows[0]?.carrierCustomerTierId
        : null;
    const restricted =
      rule.eligibilityRows.length !== 1 ||
      rule.eligibilityRows.some(
        (row) => row.customerId || row.carrierServiceId || row.dispatchCenterId
      );
    setForm({
      eligibilityRows: rule.eligibilityRows.map(
        ({
          customerId,
          carrierCustomerTierId,
          carrierServiceId,
          dispatchCenterId,
        }) => ({
          customerId,
          carrierCustomerTierId,
          carrierServiceId,
          dispatchCenterId,
        })
      ),
      funding: rule.funding,
      id: rule.id,
      name: rule.name,
      percentage: String(rule.percentage),
      status: rule.status,
      tierId: restricted ? SAVED_RESTRICTIONS : (simpleTierId ?? ALL_CUSTOMERS),
    });
  };

  const saveRule = () => {
    if (!carrierId) {
      toast.error("Select a carrier first.");
      return;
    }

    if (!form.funding) {
      toast.error("Choose who pays for this discount.");
      return;
    }
    const percentage = Number(form.percentage);
    if (form.name.trim() === "") {
      toast.error("Rule name is required.");
      return;
    }
    if (!Number.isFinite(percentage) || percentage < 0 || percentage > 100) {
      toast.error("Discount must be between 0% and 100%.");
      return;
    }

    const payload = {
      funding: form.funding,
      carrierId,
      name: form.name.trim(),
      percentage,
      status: form.status,
      stackPolicy: "EXCLUSIVE" as const,
      eligibilityRows:
        form.eligibilityRows ??
        (form.tierId === ALL_CUSTOMERS
          ? [{}]
          : [{ carrierCustomerTierId: form.tierId }]),
    };
    const options = {
      onSuccess: () => {
        resetForm();
        toast.success("Label discount rule saved.");
      },
      onError: (err: unknown) => {
        const e = err as { response?: { data?: { error?: string } } };
        toast.error(
          e.response?.data?.error ?? "Failed to save label discount rule."
        );
      },
    };

    if (form.id) {
      updateRule.mutate({ ...payload, id: form.id }, options);
      return;
    }
    createRule.mutate(payload, options);
  };

  const archiveRule = (rule: CarrierLabelDiscountRule) => {
    if (!carrierId) return;
    updateRule.mutate(
      { carrierId, id: rule.id, status: "ARCHIVED" },
      {
        onSuccess: () => toast.success("Label discount rule archived."),
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error(e.response?.data?.error ?? "Failed to archive rule.");
        },
      }
    );
  };

  const removeRule = (rule: CarrierLabelDiscountRule) => {
    if (!carrierId) return;
    if (!confirm(`Delete label discount rule "${rule.name}"?`)) return;
    deleteRule.mutate(
      { carrierId, id: rule.id },
      {
        onSuccess: () => toast.success("Label discount rule deleted."),
        onError: (err: unknown) => {
          const e = err as { response?: { data?: { error?: string } } };
          toast.error(e.response?.data?.error ?? "Failed to delete rule.");
        },
      }
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Label discount rules</CardTitle>
            <CardDescription>
              Requested label discounts. Choose who pays; the quote determines
              the amount that can actually apply.
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={resetForm}
            disabled={!carrierId}
          >
            New rule
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!carrierId ? (
          <div className="rounded-md border px-3 py-2 text-sm text-muted-foreground">
            Select a carrier to manage its label discount rules.
          </div>
        ) : (
          <>
            <div className="grid gap-3 lg:grid-cols-3">
              <Input
                type="text"
                value={form.name}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, name: event.target.value }))
                }
                placeholder="Rule name"
                aria-label="Label discount rule name"
              />
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={form.percentage}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      percentage: event.target.value,
                    }))
                  }
                  className="text-right tabular-nums"
                  aria-label="Label discount percentage"
                />
                <span className="text-sm text-muted-foreground">%</span>
              </div>
              <Select
                value={form.status}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    status: value as LabelDiscountFormState["status"],
                  }))
                }
              >
                <SelectTrigger aria-label="Label discount status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="DRAFT">Draft</SelectItem>
                  <SelectItem value="ARCHIVED">Archived</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={form.tierId}
                disabled={isSaving || form.tierId === SAVED_RESTRICTIONS}
                onValueChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    tierId: value,
                    eligibilityRows: null,
                  }))
                }
              >
                <SelectTrigger aria-label="Label discount eligibility">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {form.tierId === SAVED_RESTRICTIONS && (
                    <SelectItem value={SAVED_RESTRICTIONS}>
                      Saved restrictions preserved
                    </SelectItem>
                  )}
                  <SelectItem value={ALL_CUSTOMERS}>All customers</SelectItem>
                  {(tiers.data ?? []).map((tier) => (
                    <SelectItem key={tier.id} value={tier.id}>
                      {tier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div>
                <label
                  className="mb-1 block text-sm font-medium"
                  htmlFor="label-discount-funding"
                >
                  Who pays for this discount?
                </label>
                <Select
                  value={form.funding}
                  disabled={isSaving}
                  onValueChange={(funding) => {
                    if (funding === "CARRIER" || funding === "PLATFORM")
                      setForm((prev) => ({ ...prev, funding }));
                  }}
                >
                  <SelectTrigger
                    id="label-discount-funding"
                    aria-describedby="label-discount-funding-help"
                  >
                    <SelectValue placeholder="Choose who pays" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CARRIER">Carrier pays</SelectItem>
                    <SelectItem value="PLATFORM">EZX pays</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                onClick={saveRule}
                disabled={isSaving || tiers.isLoading || form.funding === ""}
              >
                {isSaving ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <Save className="h-3 w-3 mr-1" />
                )}
                {form.id ? "Update" : "Create"}
              </Button>
            </div>

            <p
              id="label-discount-funding-help"
              className="text-xs text-muted-foreground"
            >
              {form.funding === "CARRIER"
                ? "Carrier funding reduces the carrier's earnings on self-rated labels; EZX's fee stays unchanged. Confirm the carrier's agreement. It cannot apply to labels purchased through ChitChats or another provider, and does not switch to EZX funding."
                : form.funding === "PLATFORM"
                  ? "EZX funding uses EZX's fee on each quote, including any service-specific override. Carrier earnings stay unchanged. If that fee is zero, no discount applies; insufficient fees limit the discount."
                  : "Choose deliberately: carrier funding reduces carrier earnings; EZX funding is limited to EZX's fee. Saving an active rule does not guarantee the full requested discount."}
            </p>

            {rules.isLoading || tiers.isLoading ? (
              <div className="space-y-2">
                {[1, 2].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : rules.error ? (
              <div className="rounded-md border border-destructive/30 px-3 py-2 text-sm text-destructive">
                Label discount rules could not load.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Requested discount</TableHead>
                    <TableHead>Who pays</TableHead>
                    <TableHead>Eligibility</TableHead>
                    <TableHead className="w-[180px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(rules.data ?? []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-muted-foreground">
                        No label discount rules.
                      </TableCell>
                    </TableRow>
                  )}
                  {(rules.data ?? []).map((rule) => (
                    <TableRow key={rule.id}>
                      <TableCell className="font-medium">{rule.name}</TableCell>
                      <TableCell>{rule.status.toLowerCase()}</TableCell>
                      <TableCell className="font-mono tabular-nums">
                        {rule.percentage}%
                      </TableCell>
                      <TableCell>
                        {rule.funding === "CARRIER"
                          ? "Carrier pays"
                          : "EZX pays"}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatLabelRuleEligibility(rule, tierNameById)}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => editRule(rule)}
                          >
                            Edit
                          </Button>
                          {rule.status !== "ARCHIVED" && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => archiveRule(rule)}
                              disabled={updateRule.isPending}
                            >
                              Archive
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRule(rule)}
                            disabled={deleteRule.isPending}
                            className="h-8 w-8 p-0"
                            title="Delete rule"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function formatLabelRuleEligibility(
  rule: CarrierLabelDiscountRule,
  tierNameById: Map<string, string>
): string {
  if (rule.eligibilityRows.length === 0) return "All customers";
  return rule.eligibilityRows
    .map((row) => {
      if (
        !row.customerId &&
        !row.carrierCustomerTierId &&
        !row.carrierServiceId &&
        !row.dispatchCenterId
      ) {
        return "All customers";
      }
      if (row.carrierCustomerTierId) {
        return `Tier: ${
          tierNameById.get(row.carrierCustomerTierId) ??
          row.carrierCustomerTierId.slice(0, 8)
        }`;
      }
      if (row.customerId) return `Customer: ${row.customerId.slice(0, 8)}`;
      if (row.carrierServiceId) {
        return `Service: ${row.carrierServiceId.slice(0, 8)}`;
      }
      if (row.dispatchCenterId) {
        return `Center: ${row.dispatchCenterId.slice(0, 8)}`;
      }
      return "Custom eligibility";
    })
    .join(", ");
}
